import { useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useValidateOrderByQR } from '../../../application/hooks';
import { QRScanner } from '../../../../../common/components/QRScanner';

import {
  setNotification,
  setToogleNotification,
} from '../../../../../common/components/QRNotification/notificationQRSlice';
import { setOrdersModalConfig, setScanQROrder } from '../../../../orderSlice';
import useMobile from '../../../../../../hooks/useMobile';
import { LoadingBackdrop } from '../../../../../common';
import { useHandleScanValidationError } from '../../hooks';

export default function ReceiveOrderQRScannerViewer({ open, onClose }) {
  const { t } = useTranslation(['title', 'button', 'validation']);
  const { scanQROrder } = useSelector((store) => store.orders);
  const isMobile = useMobile();
  const dispatch = useDispatch();

  const { data, isInitialLoading, isSuccess } = useValidateOrderByQR({
    idOrder: scanQROrder,
  });

  const handleClose = useCallback(() => {
    dispatch(setScanQROrder(null));
    onClose();
  }, [dispatch, onClose]);

  useHandleScanValidationError({ handleClose });

  useEffect(() => {
    if (isSuccess) {
      dispatch(
        setOrdersModalConfig({
          titleOpening: t('orders.retrieve.modal.wish_pickup_order', {
            ns: 'description',
          }),
          show: true,
          order: { ...data?.data, qr_code: true },
          modalType: 'toRetrieve',
        })
      );

      handleClose();
    }
  }, [isSuccess, dispatch, t, data, handleClose]);

  const handleResult = useCallback(
    async (data, scanner) => {
      try {
        const scanLink = new URL(data);
        const id_order = scanLink.searchParams.get('data');

        if (id_order !== null && id_order !== undefined) {
          scanner.stop();
          scanner.destroy();
          dispatch(setScanQROrder(id_order));
        } else {
          dispatch(
            setNotification({
              message: t('qr.encodedata_no_exist', { ns: 'validation' }),
              type: 'error',
              color: 'error',
            })
          );

          dispatch(setToogleNotification(true));
        }
      } catch (error) {
        dispatch(
          setNotification({
            message: t('qr.invalid_url', { ns: 'validation' }),
            type: 'error',
            color: 'error',
          })
        );
        dispatch(setToogleNotification(true));
      }
    },
    [dispatch, t]
  );

  if (isInitialLoading) {
    return (
      <div>
        <LoadingBackdrop
          loadingComponent={<CircularProgress color="inherit" />}
        />
      </div>
    );
  }

  return (
    <Dialog open={open} fullScreen={isMobile}>
      <DialogContent>
        <Typography
          component="h1"
          variant="h1"
          color="primary"
          sx={{ mt: 4, mb: 2, textAlign: 'center' }}
        >
          {t('qr_code', { ns: 'title' })}
        </Typography>

        <Typography component="p" variant="body1" mb={4}>
          {t('qr_code_label', { ns: 'description' })}
        </Typography>

        <QRScanner handleResult={handleResult} />

        <Button
          variant="contained"
          fullWidth
          color="secondary"
          onClick={onClose}
          sx={{ color: 'inherit' }}
        >
          {t('return', { ns: 'button' })}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
